import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import Grid from "../components/Grid"; // Import the Grid component
import { FaEdit } from "react-icons/fa";
import { toast } from "react-toastify";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa";

// Use the environment variable for the API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Location({ onAddLocationClick }) {
  const [location, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [userRole, setUserRole] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedSearchField, setSelectedSearchField] = useState("location_code");


  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          toast.error("No token found. Please log in.");
          return;
        }
  
        const response = await axios.get(`${API_BASE_URL}/locationTypes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
  
        setCategories(response.data.locationCategories || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("Error fetching location categories");
      }
    };
  
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }
  
        const userResponse = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        setUserRole(userResponse.data.role); // Set the user role
      } catch (error) {
        console.error("Error fetching user role:", error);
        toast.error("Error fetching user role.");
      }
    };
  
    fetchUserRole();
  }, []);

  useEffect(() => {
    // Fetch locations from the API when the component mounts
    const fetchLocations = async () => {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        const response = await axios.get(
          `${API_BASE_URL}/locations`,
          {
            headers: {
              Authorization: `Bearer ${token}`, // Include token in Authorization header
            },
          }
        );

        const locationsWithId = response.data.locations.map((location) => ({
          ...location,
          id: location.location_code, // Map location_code to id
        }));

        setLocations(locationsWithId); // Set the filtered locations
      } catch (error) {
        console.error("Error fetching locations:", error);
        toast.error("Error fetching locations. Please try again.");
      }
    };

    fetchLocations();
  }, []);

  const handleLocationClick = (location) => {
    // Ensure that the selected locations contains all necessary fields
    setSelectedLocation({
      ...location,
      location_name: location.location_name || "",
      location_type: location.location_type || "",
      status: location.status || "Active",
    });
  };

  const handleClosePopup = () => {
    setSelectedLocation(null);
  };

  const columns = [
    { field: "location_code", headerName: "ID", flex: 1 }, // Use id here
    { field: "location_name", headerName: "Location Name", flex: 1 },
    { field: "location_type", headerName: "Location Type", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        const status = params.row.status;
        const statusClass =
          status === "Active"
            ? "bg-[#388E3C] text-white"
            : "bg-[#E57373] text-white";

        return (
          <span className={`px-2 py-1 rounded-sm ${statusClass}`}>
            {status}
          </span>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (params) => (
        <div className="flex justify-left items-left w-10 h-10">
          <button
            onClick={() => handleLocationClick(params.row)}
            className="bg-[#28ad4b] text-white px-2 py-1 flex items-center rounded-lg"
          >
            <FaEdit className="text-white mr-2" />
            Edit
          </button>
        </div>
      ),
    },
  ];

  const handleUpdateLocation = async () => {
    try {
      const token = sessionStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        return;
      }

      await axios.put(
        `${API_BASE_URL}/locations/update/${selectedLocation.location_code}`,
        {
          ...selectedLocation,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Location Updated Successfully");
      setSelectedLocation(null);

      // Refresh the locations list
      const response = await axios.get(
        `${API_BASE_URL}/locations`,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Include token in Authorization header
          },
        }
      );

      // Map the locations to include an id field
      const locationsWithId = response.data.locations.map((location) => ({
        ...location,
        id: location.location_code,
      }));

      setLocations(locationsWithId);
    } catch (error) {
      console.error("Error updating location:", error);
      toast.error("Error updating location. Please try again.");
    }
  };
  const filteredLocations = location.filter((loc) => {
  const value = loc[selectedSearchField];
    return (
      value &&
      value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
  });
    
  const handleDownloadExcel = async () => {
    const dataToExport =
      searchTerm.trim().length > 0 ? filteredLocations : location;

    if (dataToExport.length === 0) {
      toast.warn("No location data to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Locations");

    // Header
    const headerRow = [
      "Location Code",
      "Location Name",
      "Location Type",
      "Status",
    ];
    worksheet.addRow(headerRow);

    // Style header
    const header = worksheet.getRow(1);
    header.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" }, // Light yellow
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Data rows
    dataToExport.forEach((loc) => {
      worksheet.addRow([
        loc.location_code,
        loc.location_name,
        Array.isArray(loc.location_type)
          ? loc.location_type.join(", ")
          : loc.location_type,
        loc.status,
      ]);
    });

    worksheet.columns.forEach((column) => {
      column.width = column.header
        ? column.header.toString().length < 20
          ? 20
          : column.header.toString().length
        : 20;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "locations.xlsx");
  };

  function LocationMarkerEdit({ selectedLocation, setSelectedLocation }) {
    useMapEvents({
      click(e) {
        const { lat, lng } = e.latlng;
        setSelectedLocation({
          ...selectedLocation,
          latitude: lat,
          longitude: lng,
        });
      },
    });
  
    return (
      <Marker position={[selectedLocation.latitude, selectedLocation.longitude]} />
    );
  }
  

  return (
    <div className="flex h-screen">
      {/* Main Content */}
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-700">Locations</h1>
          {/* <button
            onClick={onAddLocationClick}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg"
          >
            Add Location
          </button> */}
          {userRole === "Company Admin" && (
            <button
              onClick={onAddLocationClick}
              className="bg-green-700 text-white px-4 py-2 rounded-lg"
            >
              Add Location
            </button>
          )}
        </div>

        {/* Search and Filter */}
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="location_code">Location Code</option>
                <option value="location_name">Location Name</option>
                <option value="location_type">Location Type</option>
                <option value="status">Status</option>
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search location here"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>          

          {/* Location Grid */}
          <Grid rows={filteredLocations} columns={columns} getRowId={(row) => row.location_code} />
        </div>

        {/* Popup for Location Details */}
        {selectedLocation && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative">
              <button
                onClick={handleClosePopup}
               className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-sm hover:bg-red-700"
              >
                &times;
              </button>
              <div className="flex mb-4">
                <div className="w-1/2 pl-4">
                  <h2 className="text-xl font-semibold mb-4">
                    {selectedLocation.location_code}
                  </h2>
                  {/* Location Name */}
                  <div className="mb-4">
                    <label className="block text-gray-700">Location Name</label>
                    <input
                      type="text"
                      value={selectedLocation.location_name}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      onChange={(e) =>
                        setSelectedLocation({
                          ...selectedLocation,
                          location_name: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                  <div className="mb-4">
                  <label className="block text-gray-700 mb-2">Location Type</label>
                    {categories.map((category) => (
                  <label key={category.location_category_code} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    value={category.location_category_name}
                    checked={selectedLocation.location_type.includes(category.location_category_name)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedLocation({
                          ...selectedLocation,
                          location_type: [...selectedLocation.location_type, category.location_category_name], // Add type
                        });
                      } else {
                        setSelectedLocation({
                          ...selectedLocation,
                          location_type: selectedLocation.location_type.filter(
                          (t) => t !== category.location_category_name
                          ),
                        });
                        }
                      }}
                      className="w-4 h-4 text-green-500 border-gray-300 rounded focus:ring-green-500"
                    />
                  <span>{category.location_category_name}</span>
                </label>
              ))}
              </div>
                </div>
                  {/* Status Dropdown */}
                  <div className="mb-2">
                    <label className="block text-gray-700">Status</label>
                    <select
                      value={selectedLocation.status}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      onChange={(e) =>
                        setSelectedLocation({
                          ...selectedLocation,
                          status: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Status</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                  
                </div>
              </div>
              {/* Location Map */}
              <div className="mb-6 pl-4 pr-4">
                    <label className="block text-gray-700 mb-2">Location on Map</label>
                    {selectedLocation.latitude && selectedLocation.longitude ? (
                      <MapContainer
                        center={[selectedLocation.latitude, selectedLocation.longitude]}
                        zoom={10}
                        scrollWheelZoom={true}
                        className="w-full h-64 rounded-lg"
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <LocationMarkerEdit
                          selectedLocation={selectedLocation}
                          setSelectedLocation={setSelectedLocation}
                        />
                      </MapContainer>
                      
                    ) : (
                      <p className="text-gray-500">No coordinates found for this location.</p>
                    )}
                  </div>
                  {/* Buttons */}
                  <div className="flex justify-center">
                    <button
                      onClick={handleUpdateLocation}
                      className="bg-blue-500 text-white px-4 py-2 rounded-lg"
                    >
                      Update
                    </button>
                  </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Location;
